import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { programsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FolderKanban, Plus, TrendingUp, DollarSign, Users, ListTodo } from 'lucide-react';
import { toast } from 'sonner';

export default function ProgramDashboard() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await programsAPI.analytics();
        setAnalytics(res.data);
      } catch { toast.error('Failed to load analytics'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;

  const s = analytics?.summary || {};

  const stats = [
    { label: 'Total Programs', value: s.total_programs || 0, icon: FolderKanban, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Active', value: s.active_programs || 0, icon: ListTodo, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Total Sale', value: `Rs ${(s.total_sale || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Total Cost', value: `Rs ${(s.total_cost || 0).toLocaleString()}`, icon: DollarSign, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Margin', value: `Rs ${(s.overall_margin || 0).toLocaleString()}`, icon: TrendingUp, color: s.overall_margin >= 0 ? 'text-emerald-600' : 'text-red-600', bg: s.overall_margin >= 0 ? 'bg-emerald-50' : 'bg-red-50' },
  ];

  return (
    <div className="space-y-6" data-testid="program-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Program Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage defense/aerospace programs, POs, costs & tasks</p>
        </div>
        <Button size="sm" onClick={() => navigate('/programs/new')} data-testid="new-program-btn">
          <Plus className="w-4 h-4 mr-1" /> New Program
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map(st => (
          <Card key={st.label}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${st.bg} flex items-center justify-center`}>
                  <st.icon className={`w-5 h-5 ${st.color}`} />
                </div>
                <div>
                  <p className="text-lg font-bold">{st.value}</p>
                  <p className="text-xs text-muted-foreground">{st.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* By Customer */}
      {analytics?.by_customer?.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> By Customer</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 px-2">Customer</th>
                    <th className="text-center py-2 px-2">Programs</th>
                    <th className="text-right py-2 px-2">Total Sale</th>
                    <th className="text-right py-2 px-2">Total Cost</th>
                    <th className="text-right py-2 px-2">Margin</th>
                    <th className="text-right py-2 px-2">Sale/Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.by_customer.map(c => (
                    <tr key={c.customer_id} className="border-b last:border-0">
                      <td className="py-2 px-2 font-medium">{c.customer_name}</td>
                      <td className="py-2 px-2 text-center">{c.program_count}</td>
                      <td className="py-2 px-2 text-right font-mono">Rs {c.total_sale.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right font-mono">Rs {c.total_cost.toLocaleString()}</td>
                      <td className={`py-2 px-2 text-right font-mono ${c.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Rs {c.margin.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right font-mono">{c.sale_to_cost_ratio ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Program */}
      {analytics?.by_program?.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><FolderKanban className="w-4 h-4" /> All Programs</h3>
            <div className="space-y-2">
              {analytics.by_program.map(p => (
                <div key={p.id} className="flex items-center justify-between p-2.5 rounded border bg-muted/30 cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/programs/${p.id}`)} data-testid={`program-${p.id}`}>
                  <div>
                    <p className="text-xs font-medium">{p.program_name} <span className="text-muted-foreground font-mono">({p.program_number})</span></p>
                    <p className="text-[10px] text-muted-foreground">{p.customer_name} | {p.po_count} POs | {p.task_count} tasks ({p.tasks_completed} done)</p>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <p className="text-xs font-mono">Sale: Rs {p.total_sale.toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground font-mono">Cost: Rs {p.total_cost.toLocaleString()}</p>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${p.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'}`}>
                      {p.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
